import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiResponse } from '../../../shared/models/api.response';
import { AssessmentApi, CompanyPolicyDocumentApi, ProcedureDocumentApi } from '../../../shared/api.url';
import { DtModelForInlineFilterCompanyPolicy } from '../../../shared/models/datatable.model';
import { BehaviorSubject, catchError, Observable, throwError, of, map, tap } from 'rxjs';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PolicyselectionService {
  policyid: any;
  companyQuestionid: any;
  policySelectionObject: DtModelForInlineFilterCompanyPolicy;
  isPolicyDescription: boolean;
  isPolicyComment: boolean;
  isRelatedPolicy: boolean;
  currentCompanyId: number;
  currentEntityId: number;


  private cache: Map<string, { data: any, timestamp: number }> = new Map();

  private cacheExpiration = 5 * 60 * 1000;

  private categoryId: BehaviorSubject<number>;
  private categoryName: BehaviorSubject<string>;

  private messageForEntity = new BehaviorSubject<boolean>(false); 
  entityValue = this.messageForEntity.asObservable(); 

  private messageForEditPolicy = new BehaviorSubject<boolean>(false); 
  editPolicyValue = this.messageForEditPolicy.asObservable(); 

  private messageForAddEditPolicyQuestion = new BehaviorSubject<boolean>(false); 
  editAddPolicyQuestionValue = this.messageForEditPolicy.asObservable(); 

  private companyPolicyDocumentId: BehaviorSubject<number>;
  private companyPolicyDocumentFileName: BehaviorSubject<string>;
  apiUrl = environment.apiBaseUrl
  
  constructor(public http: HttpClient) {
    this.categoryId = new BehaviorSubject(0);
    this.categoryName = new BehaviorSubject("");
    this.companyPolicyDocumentId = new BehaviorSubject(0);
    this.companyPolicyDocumentFileName = new BehaviorSubject("");
  }

  clearCache(): void {
    this.cache.clear();
  }

  private isCacheValid(cacheKey: string): boolean {
    if (!this.cache.has(cacheKey)) {
      return false;
    }
    
    const cacheEntry = this.cache.get(cacheKey);
    const now = Date.now();
    return (now - cacheEntry.timestamp) < this.cacheExpiration;
  }

  private getWithCache<T>(url: string, params?: HttpParams, cacheKey?: string): Observable<T> {
    const key = cacheKey || `${url}${params ? '?' + params.toString() : ''}`;   
   
    if (this.isCacheValid(key)) {
      return of(this.cache.get(key).data);
    }   

    return this.http.get<T>(url, { params }).pipe(
      tap(response => {
        this.cache.set(key, { data: response, timestamp: Date.now() });
      })
    );
  }


  set mainDocumentCategory(value) {
    this.categoryId.next(value);
  }

  get mainDocumentCategory(): any | Observable<number> {
    return this.categoryId.asObservable();
  }

  set mainDocumentCategoryName(value) {
    this.categoryName.next(value);
  }

  get mainDocumentCategoryName(): any | Observable<string> {
    return this.categoryName.asObservable();
  }

  set companyPolicyId(value) {
    this.companyPolicyDocumentId.next(value);
  }

  get companyPolicyId(): any | Observable<number> {
    return this.companyPolicyDocumentId.asObservable();
  }

  set companyPolicyFileName(value) {
    this.companyPolicyDocumentFileName.next(value);
  }

  get companyPolicyFileName(): any | Observable<string> {
    return this.companyPolicyDocumentFileName.asObservable();
  }


  addEditEntity(message: boolean) {
    this.messageForEntity.next(message);
    this.clearCache(); 
  }

  editPolicyChanges(message: boolean) {
    this.messageForEditPolicy.next(message);
    this.clearCache(); 
  }

  AddeditPolicyQuestionChanges(message: boolean) {
    this.messageForAddEditPolicyQuestion.next(message);
    this.clearCache(); 
  }

  getPolicyList(model: any) {
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.getCompanySelectionPolicy, model);
  }

  getPolicySelectionConfiguration(companyId: number, entityId: number) {
    let params = new HttpParams().set('companyId', companyId.toString()).set('entityId', entityId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getPolicySelectionConfiguration, 
      params, 
      `policySelectionConfig-${companyId}-${entityId}`
    );
  }

  setPolicySelectionConfiguration(model) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.setPolicySelectionConfiguration, model);
  }

  getCategoryList(companyId: number) {
    let params = new HttpParams().set('companyId', companyId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getCategory, 
      params, 
      `categoryList-${companyId}`
    );
  }

  getCategoryListFromControl(model: any) {
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.getCategoryFromControl, model);
  }
   
  getControlListFromCategory(model: any) {
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.getControlListFromCategory, model);
  }

  getControlListFromRemoveControl(model: any) {
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.getControlListFromRemoveControl, model);
  }

  getMainCategoryFromAnyCategory(model: any) {
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.getMainCategoryFromAnyCategory, model);
  }

  getComplianceList(CompanyId: number) {
    if (CompanyId == 0 || CompanyId == undefined)
      CompanyId = 0;

    let params = new HttpParams().set('companyId', CompanyId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getComplianceList, 
      params, 
      `complianceList-${CompanyId}`
    );
  }

  getQuestionCategoryListFromCompany(CompanyId: number) {
    
    if (CompanyId == 0 || CompanyId == undefined)
      CompanyId = 0;

    let params = new HttpParams().set('companyId', CompanyId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getQuestionCategoryList, 
      params, 
      `questionCategoryList-${CompanyId}`
    );
  }
  
  getQuestionList(model: any) {
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.getCompanySelectionPolicyQuestion, model);
  }

  setPolicyStatus(model: any) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.setPolicySelectionStatus, model);
  }

  GetPDFReportPolicySelectionList(model: any) {
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.generateExportReport, model);
  }

  changePolicySatatus(model: any) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.setStatusOfPolicy, model);
  }

  getPolicySelection(policyId: number, companyId: number) {
    let params = new HttpParams().set('policyId', policyId.toString()).set('companyId', companyId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getPolicyOfPolicySelection, 
      params, 
      `policySelection-${policyId}-${companyId}`
    );
  }

  // POST methods
  editPolicySelection(model: any) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.policySelectionEdit, model);
  }

  getPolicyQuestion(id: number, companyId: number, entityId: number) {
    if (id == 0 || id == undefined)
      id = 0;

    if (companyId == 0 || companyId == undefined)
      companyId = 0;

    let params = new HttpParams().set('id', id).set('companyId', companyId).set('entityId', entityId);
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getpolicySelectionQuestion, 
      params, 
      `policyQuestion-${id}-${companyId}-${entityId}`
    );
  }

  addOrEditQuestionInPolicySelection(model: any) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.addOrEditQuestionInPolicySelection, model);
  }

  deletePolicyQuestion(id: number) {
    let params = new HttpParams().set("id", id);
    return this.http.get<ApiResponse>(this.apiUrl + AssessmentApi.deletePolicyQuestion, { params: params });
  }


  getQuestionSetFromPolicyId(id: number, companyId: number) {
    if (id == 0 || id == undefined)
      id = 0;

    if (companyId == 0 || companyId == undefined)
      companyId = 0;

    let params = new HttpParams().set('policyId', id.toString()).set('companyId', companyId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getQuestionSetFromPolicyId, 
      params, 
      `questionSet-${id}-${companyId}`
    );
  }

  getLastActiveQuestionNo(id: number, questionSetId: number, companyId: number) {
    if (id == 0 || id == undefined)
      id = 0;

    if (questionSetId == 0 || questionSetId == undefined)
      questionSetId = 0;

    let params = new HttpParams().set('policyId', id.toString()).set('questionSetId', questionSetId.toString()).set('companyId', companyId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getLastQuestionAndSubQuestionNo, 
      params, 
      `lastActiveQuestion-${id}-${questionSetId}-${companyId}`
    );
  }

  getControlListFromControl(compliances: any) {
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.getControlListFromControl, compliances);
  }

  getControlList(ComplianceId: number) {
    if (ComplianceId == 0 || ComplianceId == undefined)
      ComplianceId = 0;

    let params = new HttpParams().set('complianceId', ComplianceId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getControlList, 
      params, 
      `controlList-${ComplianceId}`
    );
  }

  setControlListOfCompliance(model: any) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.setControlListOfCompliance, model);
  }

  setCategoryForPolicySelection(model: any) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.setCategoryForPolicySelection, model);
  }

  getCategoryFromCompliance(model: any) {
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.checkComplianaceControlAll, model);
  }

  getCategoryFromOneCompliance(complianceId: number) {
    let params = new HttpParams().set('complianceId', complianceId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getCategoryFromOneCompliance, 
      params, 
      `categoryFromCompliance-${complianceId}`
    );
  }
 
  getSelectedEntityOfCompany(companyId: number) {
    let params = new HttpParams().set('companyId', companyId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getSelectedEntityByCompany, 
      params, 
      `selectedEntity-${companyId}`
    );
  }

  getEntitiesByCompany(companyId: number) {
    let params = new HttpParams().set('companyId', companyId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getEntitiesByCompany, 
      params, 
      `entities-${companyId}`
    );
  }

  addEntity(model: any) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.addEntity, model);
  }

  editEntity(model: any) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.editEntity, model);
  }

  setEntityToCompany(model: any) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.setEntityToCompany, model);
  }

  setSetting(model: any) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.setSetting, model);
  }

  getCompanyName(companyId: number) {
    let params = new HttpParams().set('companyId', companyId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getCompanyName, 
      params, 
      `companyName-${companyId}`
    );
  }

  getCompanyPolicyDocumentOfCompanyList(model: any) {
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.getCompanyPolicyDocumentOfCompanyList, model);
  }

  downloadCompanyPolicyDocumentFile(id: number) {
    
    let params = new HttpParams().set("companyPolicyDocumentId", id.toString());
    return this.http.get<ApiResponse>(
      this.apiUrl + CompanyPolicyDocumentApi.downloadCompanyPolicyDocumentApiUrl,
      { params }
    );
  }
  
  viewCompanyPolicyDocumentFile(id: number) {
    let params = new HttpParams().set("companyPolicyDocumentId", id.toString());
    return this.http.get<ApiResponse>(
      this.apiUrl + CompanyPolicyDocumentApi.viewCompanyPolicyDocumentFileApiUrl,
      { params }
    );
  }

  getProcedureDocumentOfCompanyList(model: any) {
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.getProcedureDocumentOfCompanyList, model);
  }

  downloadProcedureDocuemntFile(id: number) {
    let params = new HttpParams().set("procedureDocumentId", id.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + ProcedureDocumentApi.downloadProcedureDocumentApiUrl, 
      params, 
      `procedureDoc-${id}`
    );
  }

  changeCompanyPolicyDocument(formData: FormData): Observable<ApiResponse> {
    this.clearCache(); 
    const url = `${this.apiUrl}${AssessmentApi.changeCompanyPolicy}`;
    return this.http.post<ApiResponse>(url, formData).pipe(
      catchError((error) => {
        console.error('API call failed:', error);
        return throwError(() => new Error('Failed to change company policy document.'));
      })
    );
  }

  changeProcedureDocument(model: any) {
    this.clearCache(); 
    return this.http.post<ApiResponse>(this.apiUrl + AssessmentApi.changeProcedure, model);
  }

  getSubTitleOfCategoryFromCompanyQuestionCategory(companyId: number, complianceId: number) {
    let params = new HttpParams().set('companyId', companyId.toString()).set('complianceId', complianceId.toString());
    return this.getWithCache<ApiResponse>(
      this.apiUrl + AssessmentApi.getSubTitleOfCategoryFromCompanyQuestionCategory, 
      params, 
      `subTitleCategory-${companyId}-${complianceId}`
    );
  }
}